import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { Badge, Button, Callout, Popover, Text, Tooltip } from '@radix-ui/themes';
import { BadgeIcon, Check, Circle, CircleCheck, CircleX, RotateCcw, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AiDetectionIcon from '../AiDetectionIcon';
import { track } from '../../analytics/track';
import { AnalyticsEvents } from '../../analytics/events';
import type { Summaries, Summary, SummaryDetail } from './Header';

const RECORDING_MEDIA_URL = gql`
  query RecordingMediaUrl($token: String!) {
    recordingMediaUrl(token: $token) {
      url
      expiresAt
      kind
    }
  }
`;

/**
 * Player for an audio/video recording answer in the summary. The durable link's token (last
 * path segment) is exchanged backend-side for a fresh short-lived streaming URL; bytes stream
 * from storage, never through our backends. Click-to-load so opening a summary stays cheap.
 */
const SummaryMediaPlayer = ({ media }: { media: NonNullable<SummaryDetail['media']> }) => {
  const { t } = useTranslation();
  const token = media.link.split('/').pop() ?? '';
  const thumbnailToken = media.thumbnailLink ? (media.thumbnailLink.split('/').pop() ?? '') : '';

  // Video answers: exchange the thumbnail link eagerly so the summary shows a visual preview
  // (like the scoring page); the video itself stays click-to-load. Audio has no thumbnail.
  const { data: thumbData } = useQuery<{ recordingMediaUrl: { url?: string } }>(RECORDING_MEDIA_URL, {
    variables: { token: thumbnailToken },
    skip: !thumbnailToken,
  });
  const thumbnailUrl = thumbData?.recordingMediaUrl?.url;

  const [load, { data, loading, error }] = useLazyQuery<{ recordingMediaUrl: { url?: string } }>(RECORDING_MEDIA_URL, {
    variables: { token },
  });
  const url = data?.recordingMediaUrl?.url;

  if (!token) return null;

  return (
    // The answer cell toggles open/close on click — keep player interactions out of that.
    <div className="my-1" onClick={(e) => e.stopPropagation()} data-ph-capture-attribute-region="media_player">
      {!url && !loading && !error && (
        <>
          {thumbnailUrl ? (
            <button
              type="button"
              onClick={() => load()}
              className="relative block overflow-hidden rounded"
              style={{ maxWidth: '16rem' }}
              aria-label={t('swiper.summary.playVideo')}
            >
              <img src={thumbnailUrl} alt="" className="block w-full rounded" />
              <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-3xl text-white">
                ▶
              </span>
            </button>
          ) : (
            <Button variant="soft" size="1" onClick={() => load()}>
              {media.kind === 'audio' ? t('swiper.summary.playAudio') : t('swiper.summary.playVideo')}
            </Button>
          )}
        </>
      )}
      {loading && (
        <Text size="1" color="gray">
          {t('swiper.summary.loadingRecording')}
        </Text>
      )}
      {(error || (data && !url)) && (
        <Text size="1" color="red">
          {t('swiper.summary.recordingUnavailable')}
        </Text>
      )}
      {url &&
        (media.kind === 'audio' ? (
          <audio
            controls
            preload="metadata"
            src={url}
            className="w-full"
            onPlay={() => track(AnalyticsEvents.ANSWER_AUDIO_PLAYED, { kind: 'audio' })}
          />
        ) : (
          <video
            onPlay={() => track(AnalyticsEvents.ANSWER_AUDIO_PLAYED, { kind: 'video' })}
            controls
            preload="metadata"
            src={url}
            poster={thumbnailUrl}
            className="w-full rounded"
            style={{ maxHeight: '16rem' }}
          />
        ))}
    </div>
  );
};

const Summary = ({ summaries, className }: { summaries: Summaries[]; className?: string }) => {
  const { t } = useTranslation();
  const [key, setKey] = useState(0);

  if (summaries.length > 0) {
    const summary = summaries[key];
    return (
      <div className={`px-4 summary ${className}`} data-ph-capture-attribute-region="summary">
        {summaries.length > 1 && (
          <div className="mb-2">
            <Button
              color="gray"
              variant={key === 0 ? 'solid' : 'soft'}
              onClick={() => {
                setKey(0);
              }}
            >
              {t('swiper.summary.button.summary1')}
            </Button>
            <Button
              color="gray"
              variant={key === 1 ? 'solid' : 'soft'}
              className="ml-2"
              onClick={() => {
                setKey(1);
              }}
            >
              {t('swiper.summary.button.summary2')}
            </Button>
          </div>
        )}
        {summary.summaryPart.some((part) => part.details.some((detail) => detail.reset)) && (
          <Callout.Root color="amber">
            <Callout.Text className="mb-0">
              {t('swiper.summary.interviewReset', {
                count: summary.summaryPart.flatMap((part) => part.details).filter((detail) => detail.reset).length,
              })}
            </Callout.Text>
          </Callout.Root>
        )}
        {summary.summaryPart.map((sum, index) => {
          return <PrintSummary key={index} summary={sum} />;
        })}
        {summary.keywordSummary && summary.keywordSummary.text && (
          <table className="w-full mb-4" style={{ fontSize: '.8rem' }}>
            <thead>
              <tr>
                <h2>{t('swiper.summary.keywordSummary')}</h2>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <pre style={{ fontSize: '.8rem', fontFamily: 'Robot, sans-serif', fontWeight: 400 }}>
                    <span dangerouslySetInnerHTML={{ __html: summary.keywordSummary.text }} />{' '}
                  </pre>
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    );
  }

  return (
    <div className="pl-4 pt-4" data-ph-capture-attribute-region="summary">
      <Callout.Root color="blue">
        <Callout.Text className="mb-0">{t('swiper.summary.notCompleted')}</Callout.Text>
      </Callout.Root>
    </div>
  );
};
export default Summary;

const PrintSummary = ({ summary }: { summary: Summary }) => {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <table className="w-full mb-4" style={{ fontSize: '.8rem' }}>
        <thead>
          <tr>
            <th colSpan={2} className={'text-left'}>
              <h2>
                {summary.header}
                {summary && summary.points ? (
                  <small className="float-right">
                    {t('swiper.summary.thresholdFormat', {
                      points: summary.points.toFixed(0),
                      maxPoints: summary.maxPoints,
                      threshold: summary.threshold,
                    })}
                  </small>
                ) : null}
              </h2>
            </th>
          </tr>
        </thead>
        <tbody>
          {summary.details.map((detail, index) => {
            return <RowDetail detail={detail} key={index} />;
          })}
        </tbody>
      </table>
    </div>
  );
};

const RowDetail = ({ detail }: { detail: SummaryDetail }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <tr className="border border-gray-200">
      <td>
        <div className="text-center summery-icon flex flex-row justify-between items-center">
          {getIcon(detail)}
          {detail && detail.aiDetection && detail.aiDetection.isAi ? <AiDetectionIcon /> : <div></div>}
          {detail && detail.reset ? (
            <Tooltip content={<Text size="3">{t('swiper.summary.resetAtQuestion')}</Text>}>
              <RotateCcw className="text-yellow-500" />
            </Tooltip>
          ) : (
            <div></div>
          )}
        </div>
      </td>

      <Popover.Root>
        <Popover.Trigger>
          <td>{detail.label}</td>
        </Popover.Trigger>
        <Popover.Content side="top" className="popup px-4 py-2">
          <Text size={'2'}>
            <div className="title">{t('swiper.summary.question')}</div>
            <div
              dangerouslySetInnerHTML={{
                __html: detail.question ? detail.question.replace(/^<br>/, '') : detail.label.replace(/^<br>/, ''),
              }}
            ></div>
          </Text>
        </Popover.Content>
      </Popover.Root>

      <td className="border border-gray-200" onClick={handleClick}>
        {detail.options && detail.options.length ? (
          <>
            {detail.options.map((option, index) => {
              if (option.evaluation) {
                return (
                  <div key={index} className="flex items-center">
                    <span dangerouslySetInnerHTML={{ __html: option.label }} />
                    <span className="ml-1">
                      {option.evaluation.name
                        ? getIcon({ icon: option.evaluation.name, bonuspoints: option.evaluation.bonus_points })
                        : null}
                    </span>
                  </div>
                );
              } else {
                const variant = option.answered ? 'soft' : 'outline';
                const color = option.isCorrect ? 'green' : 'red';
                return (
                  <Badge
                    key={`summary-test-option-${index}`}
                    variant={variant}
                    color={color}
                    radius="large"
                    className="m-2"
                  >
                    {option.label}
                  </Badge>
                );
              }
            })}
          </>
        ) : (
          <div>
            {detail.media?.link && <SummaryMediaPlayer media={detail.media} />}
            <div
              className={`${open ? 'answerOpen' : 'answerClose'} summaryAnswer`}
              dangerouslySetInnerHTML={{ __html: detail.answer ? detail.answer.replace(/^<br>/, '') : '' }}
            ></div>
          </div>
        )}
      </td>
    </tr>
  );
};

const getCert = (points: number) => {
  return (
    <div className="flex! items-center justify-center">
      <BadgeIcon className="text-bonus-points!" fill="#28a745" />
      <span style={{ fontSize: '0.8rem', position: 'absolute' }} className="text-white">
        {points}
      </span>
    </div>
  );
};

const getPointCorrect = (points: number) => {
  return (
    <div className="flex! items-center justify-center">
      <Circle className="text-green-600" fill="#28a745" />
      <span style={{ fontSize: '0.8rem', position: 'absolute' }} className="text-white">
        {points}
      </span>
    </div>
  );
};

const getPointIncorrect = (points: number) => {
  return (
    <div className="flex! items-center justify-center">
      <Circle className="text-red-600" fill="#dc3545" />
      <span style={{ fontSize: '0.8rem', position: 'absolute' }} className="text-white">
        {points}
      </span>
    </div>
  );
};

const getIcon = (summary: { icon: string; average?: number; points?: number; bonuspoints: number }) => {
  let icon;

  switch (true) {
    case summary.icon === 'accepted':
      icon = <Check className="icon text-green-600" />;
      break;
    case summary.icon === 'disqualified':
      icon = <CircleX className="icon text-disqualified!" />;
      break;
    case summary.icon === 'auto_qualified':
      icon = (
        <div className="flex! flex-row items-center justify-start">
          <CircleCheck className="text-white!" fill="#7365f2" size={32} />
          <div className="flex! items-center justify-center">
            <Circle className="text-points! bg-points rounded-full" />
            <span style={{ fontSize: '0.8rem', position: 'absolute' }} className="text-white">
              {Math.round(summary.points ?? 0)}
            </span>
          </div>
        </div>
      );
      break;

    case summary.icon === 'point':
      icon = (
        <div className="flex! items-center justify-center">
          <Circle className="text-points! bg-points rounded-full" />
          <span style={{ fontSize: '0.8rem', position: 'absolute' }} className="text-white">
            {Math.round(summary.points ?? 0)}
          </span>
        </div>
      );
      break;
    case summary.icon === 'warning':
      icon = <TriangleAlert className="icon text-warning!" />;
      break;
    case summary.icon === 'bonus':
      icon = getCert(summary.bonuspoints);
      break;
    case summary.points != null:
      if (summary.points !== 0) icon = getPointCorrect(Math.round(summary.points));
      else icon = getPointIncorrect(Math.round(summary.points));
      break;
    default:
      icon = null;
  }
  return icon;
};
